// convex/lib/config/domains/clients.ts

import { validatorClient } from "../../../companies/clients";
import { clientExemptDocumentValidator } from "../../../companies/clients/exempt_documents";
import { validatorUsers } from "../../../system/users";
import { createTagFilterRule } from "../../../companies/tags/filter_rules";
import type { DomainGroup, TableScope } from "../types";

// ============================================================================
// CLIENTS DOMAIN - Status Values for i18n
// ============================================================================

/** Tipos de cliente (para i18n) */
export const CLIENT_TYPES = ["individual", "company", "organization"] as const;

export const clientsDomain: DomainGroup = {
  id: "clients",
  name: "Clientes",
  description: "Gestión de clientes",
  icon: "Users",
  order: 2,
  status: "active",
  // Acceso por defecto de company_user a este grupo (ver default_permissions.ts)
  defaultAccess: { company_user: "full" },

  // ── Configuración i18n del dominio ────────────────────
  i18n: {
    namespace: "clients",
    keys: {
      title: "clients.title",
      description: "clients.description",
    },
  },

  modules: [
    {
      id: "clients",
      name: "Clientes",
      description: "Remitentes y destinatarios de envíos",
      icon: "UserCircle",
      permissions: ["canCreate", "canRead", "canUpdate", "canDelete"],
      routes: ["/clients"],
      navigation: {
        showInNav: true,
        contexts: ["main"],
        navOrder: 4,
        status: "active",
      },

      // ── Configuración i18n del módulo ────────────────────
      i18n: {
        namespace: "clients.clients",
        keys: {
          title: "clients.clients.title",
          description: "clients.clients.description",
          longDescription: "clients.clients.longDescription",
          searchPlaceholder: "clients.clients.searchPlaceholder",
          emptyState: "clients.clients.emptyState",
          actions: {
            create: "clients.clients.actions.create",
            edit: "clients.clients.actions.edit",
            delete: "clients.clients.actions.delete",
            view: "clients.clients.actions.view",
          },
        },
        translatableFields: ["notes"],
        translatableEnums: [{ field: "type", values: CLIENT_TYPES }],
      },

      tables: [
        {
          tableName: "clients",
          schema: validatorClient,
          scriptActions: ["create", "update", "updateMany", "delete", "deleteMany"],
          // `globals.clients.get/set/...` para configs compartidas.
          scriptGlobals: true,
          // `clients` no tiene campo `status` propio: el estado vive en
          // `internalControl.state`. El admin puede escribir
          // `where({ status: "active" })` y el sandbox traduce.
          scriptFieldAliases: {
            status: "internalControl.state",
            createdAt: "internalControl.createdAt",
            updatedAt: "internalControl.updatedAt",
          },
          scope: "company" as TableScope,
          masterData: true,
          relations: [
            {
              key: "lastUpdatedBy",
              sourceField: "internalControl.updatedBy",
              schema: validatorUsers,
              tableName: "users",
            },
            {
              key: "lastCreatedBy",
              sourceField: "internalControl.createdBy",
              schema: validatorUsers,
              tableName: "users",
            },
          ],
          searchFields: ["name", "code", "email"],
          hasStatusIndex: false,
          customRules: [createTagFilterRule("clients")],
          withTags: true,
          searchIndex: {
            name: "search_clients",
            field: "name",
          },
        },
      ],
      readOnlyTables: [{ tableName: "branches" }],
    },
    {
      id: "client_exemptions",
      name: "Exenciones de Cliente",
      description: "Documentos de exención fiscal de clientes",
      icon: "FileText",
      permissions: ["canCreate", "canRead", "canUpdate", "canDelete"],
      routes: ["/clients/:id/exemptions"],
      navigation: {
        showInNav: false,
        contexts: [],
        navOrder: 0,
        status: "active",
      },

      // ── Configuración i18n del módulo ────────────────────
      i18n: {
        namespace: "clients.client_exemptions",
        keys: {
          title: "clients.client_exemptions.title",
          description: "clients.client_exemptions.description",
          longDescription: "clients.client_exemptions.longDescription",
          searchPlaceholder: "clients.client_exemptions.searchPlaceholder",
          emptyState: "clients.client_exemptions.emptyState",
          actions: {
            create: "clients.client_exemptions.actions.create",
            edit: "clients.client_exemptions.actions.edit",
            delete: "clients.client_exemptions.actions.delete",
            view: "clients.client_exemptions.actions.view",
          },
        },
        translatableFields: ["notes", "rejectionReason"],
        translatableEnums: [
          {
            field: "status",
            values: ["pending", "approved", "rejected", "expired"],
          },
        ],
      },

      tables: [
        {
          tableName: "clients_exempt_documents",
          schema: clientExemptDocumentValidator,
          scope: "company" as TableScope,
          relations: [
            {
              key: "client",
              sourceField: "clientId",
              schema: validatorClient,
              tableName: "clients",
            },
            {
              key: "approvedBy",
              schema: validatorUsers,
              tableName: "users",
            },
            {
              key: "lastUpdatedBy",
              sourceField: "internalControl.updatedBy",
              schema: validatorUsers,
              tableName: "users",
            },
            {
              key: "lastCreatedBy",
              sourceField: "internalControl.createdBy",
              schema: validatorUsers,
              tableName: "users",
            },
          ],
          searchFields: ["fiscalCode"],
          hasStatusIndex: true,
          customRules: [],
        },
      ],
      readOnlyTables: [{ tableName: "branches" }],
    },
  ],
};
